import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { estApi } from "../lib/api";
import EmailProposalDialog from "./EmailProposalDialog";

// Email proposal: one GC goes straight to the composer; 2+ GCs first pick who gets it, then each
// picked GC gets its own email + PDF (its company and contacts in the To), one after another.
export default function ProposalEmailFlow({ open, onOpenChange, estimateId, onSent }) {
  const [companies, setCompanies] = useState(null);
  const [picked, setPicked] = useState([]);
  const [queue, setQueue] = useState(null);

  useEffect(() => {
    if (!open) { setCompanies(null); setQueue(null); return; }
    estApi.getEmailDraft(estimateId).then((d) => {
      const list = d.companies || [];
      const ready = list.filter((c) => c.contacts.length);
      const missing = list.filter((c) => !c.contacts.length);
      if (missing.length) toast.warning(`${missing.map((c) => c.name).join(", ")} ${missing.length > 1 ? "need" : "needs"} a contact before the proposal can be emailed`);
      if (list.length && !ready.length) { onOpenChange(false); return; }
      setCompanies(list);
      setPicked(ready.map((c) => c.id));
      if (list.length <= 1) setQueue(list.length ? [list[0]] : [{ id: "", name: "" }]);
    }).catch(() => { toast.error("Could not build the email draft"); onOpenChange(false); });
  }, [open, estimateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = queue?.[0];
  const next = () => setQueue((q) => { const rest = (q || []).slice(1); if (!rest.length) onOpenChange(false); return rest; });
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <>
      <Dialog open={open && !queue} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="proposal-pick-customers">
          <DialogHeader><DialogTitle>Email the proposal to</DialogTitle><DialogDescription className="text-xs">Pick the customers that should get their own proposal.</DialogDescription></DialogHeader>
          {!companies ? (
            <div className="flex items-center justify-center py-8 text-xs text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading customers…</div>
          ) : (
            <div className="space-y-2">
              {companies.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-start gap-2.5 rounded-md border border-slate-200 p-2.5 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                       data-testid={`proposal-pick-${c.id}`}>
                  <Checkbox checked={picked.includes(c.id)} disabled={!c.contacts.length} onCheckedChange={() => toggle(c.id)} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{c.name || "Unnamed customer"}</span>
                    <span className="block truncate text-[11px] text-slate-500">
                      {c.contacts.length ? c.contacts.map((p) => p.name || p.email).join(", ")
                        : <span className="text-amber-600">Needs a contact — add one in Info › Customer(s)</span>}
                    </span>
                  </span>
                </label>))}
              <p className="text-[11px] text-slate-500">Each customer gets its own email and its own proposal PDF addressed to them.</p>
            </div>)}
          <DialogFooter>
            <Button variant="nav" onClick={() => onOpenChange(false)} data-testid="proposal-pick-cancel">Cancel</Button>
            <Button variant="nav" disabled={!picked.length} data-testid="proposal-pick-continue"
                    onClick={() => setQueue(companies.filter((c) => picked.includes(c.id)))}>
              Continue ({picked.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {open && current && (
        <EmailProposalDialog key={current.id || "all"} open estimateId={estimateId} companyId={current.id}
                             restrictLabel={current.name}
                             restrictEmails={current.id ? current.contacts.map((p) => p.email) : undefined}
                             onOpenChange={(v) => { if (!v) next(); }}
                             onSent={(res) => onSent?.(res)} />
      )}
    </>
  );
}
